import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { BirthdayBanner } from "../components/BirthdayBanner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { JourneyView, StartHereBanner, StartHereGate, TierHeader, FutureTracks, TIER_ORDER } from "../components/commercial/JourneyView";
import { useProgress } from "../contexts/ProgressContext";
import { CategoryIcon } from "../components/CategoryIcon";
import { NewInVaultAnnouncement } from "../components/NewInVaultAnnouncement";
import { SystemStep } from "../components/SystemStep";
import { HowToWalkthrough } from "../components/HowToWalkthrough";
import { StartHereStrip } from "../components/StartHereStrip";
import { NextDropsPreview } from "../components/NextDropsPreview";
import { FastestPathToThousand } from "../components/FastestPathToThousand";
import { FastMonetisationDiscoveryBanner } from "../components/FastMonetisationDiscoveryBanner";
import { CATEGORY_PILLS, TRACK_CATEGORY_MAP } from "../data/track_categories";
import { getDisplayName } from "../lib/displayName";
import { isNewTrack } from "../lib/isNewTrack";
import { ArrowRight, ArrowUpRight, LayoutGrid, List, Lock, Flame, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { VAULT } from "../data/vaultStats";
import { AffiliateInvite } from "../components/AffiliateInvite";
import { DemandBadge } from "../components/DemandBadge";
import { VaultDownloadButton, ModelBadge } from "../components/TrackDownloads";

const VIEW_STORAGE_KEY = "stagvault_dashboard_view";
const STREAK_DISMISS_KEY = "stagvault_streak_save_dismissed";

export default function Dashboard() {
  const { user } = useAuth();
  const { countFor } = useProgress();
  const [readyStates, setReadyStates] = useState({});

  useEffect(() => { api.get("/track-states").then((r) => setReadyStates(r.data.states || {})).catch(() => {}); }, []);
  const [planDays, setPlanDays] = useState({});
  useEffect(() => { api.get("/plans").then((r) => setPlanDays(r.data || {})).catch(() => {}); }, []);
  const isPaid = user && typeof user === "object" && (user.tier === "lifetime" || user.tier === "monthly");
  const FREE_LIMIT = 6;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streakSave, setStreakSave] = useState(null); // { previous_streak } | null
  const [restoring, setRestoring] = useState(false);
  // Filter state is URL-synced so `/vault?filter=commerce` deep-links land on
  // the right pill. Clicking a pill also updates the URL so the filter can be
  // shared / bookmarked. Falls back to "all" if the param is missing/invalid.
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = useMemo(() => CATEGORY_PILLS.map((p) => p.key), []);
  const urlFilter = searchParams.get("filter");
  const initialFilter = validKeys.includes(urlFilter) ? urlFilter : "all";
  const [activeFilter, setActiveFilterState] = useState(initialFilter);

  const setActiveFilter = (key) => {
    setActiveFilterState(key);
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("filter");
    else next.set("filter", key);
    setSearchParams(next, { replace: true });
  };

  // Keep local state in sync if the URL changes externally (browser back/forward)
  useEffect(() => {
    const fromUrl = searchParams.get("filter");
    const next = validKeys.includes(fromUrl) ? fromUrl : "all";
    setActiveFilterState((prev) => (prev === next ? prev : next));
  }, [searchParams, validKeys]);
  // Filter categories by the active pill. Elite Prompts is pinned separately
  // and never filtered. Per-pill counts computed once so the labels update.
  const filteredCategories = useMemo(() => {
    if (activeFilter === "all") return categories;
    return categories.filter((c) => TRACK_CATEGORY_MAP[c.slug] === activeFilter);
  }, [categories, activeFilter]);

  const [gateOpen, setGateOpen] = useState(() => !localStorage.getItem("sv_start_here_choice") && localStorage.getItem("sv_start_here_dismissed") !== "1");
  const tieredCategories = useMemo(() => [...filteredCategories].sort((a, b) => TIER_ORDER.indexOf(a.tier || "classic") - TIER_ORDER.indexOf(b.tier || "classic") || a.order - b.order), [filteredCategories]);
  const tierCounts = useMemo(() => tieredCategories.reduce((m, c) => ({ ...m, [c.tier || "classic"]: (m[c.tier || "classic"] || 0) + 1 }), {}), [tieredCategories]);
  const skipGate = () => { localStorage.setItem("sv_start_here_dismissed", "1"); setGateOpen(false); };
  const pillCounts = useMemo(() => {
    const counts = { all: categories.length };
    for (const p of CATEGORY_PILLS) {
      if (p.key === "all") continue;
      counts[p.key] = categories.filter((c) => TRACK_CATEGORY_MAP[c.slug] === p.key).length;
    }
    return counts;
  }, [categories]);

  const activePillLabel = useMemo(
    () => CATEGORY_PILLS.find((p) => p.key === activeFilter)?.label || "All",
    [activeFilter],
  );
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "grid";
    return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
  });

  const changeView = (v) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // ignore quota / privacy-mode errors
    }
  };

  useEffect(() => {
    let mounted = true;
    api
      .get("/categories")
      .then((res) => {
        if (mounted) setCategories(res.data);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Streak-save detection — waits for Header's touch to land, then checks.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const dismissed = (() => {
      try { return sessionStorage.getItem(STREAK_DISMISS_KEY) === "1"; } catch { return false; }
    })();
    if (dismissed) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/usage/streak");
        if (cancelled) return;
        if (data?.can_restore && data?.previous_streak > 1) {
          setStreakSave({ previous_streak: data.previous_streak });
        }
      } catch {
        // silent
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user]);

  const restoreStreak = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/usage/streak/restore");
      toast.success(`Streak saved — back to ${data.streak} days. Don't drop it again.`);
      setStreakSave(null);
      try {
        window.dispatchEvent(new CustomEvent("stagvault:streak-changed", { detail: { streak: data.streak } }));
      } catch {
        // ignore
      }
    } catch {
      toast.error("Couldn't save your streak.");
    } finally {
      setRestoring(false);
    }
  };

  const dismissStreak = () => {
    try { sessionStorage.setItem(STREAK_DISMISS_KEY, "1"); } catch {
      // ignore privacy-mode errors
    }
    setStreakSave(null);
  };

  const totalPrompts = categories.reduce((acc, c) => acc + c.prompt_count, 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dashboard-page">
      <Header />
      <BirthdayBanner compact />

      {streakSave && (
        <div className="border-b border-amber-500/30 bg-amber-500/[0.05]" data-testid="streak-save-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-sm text-amber-100/90">
              <Flame size={16} className="text-amber-300 drop-shadow-[0_0_10px_rgba(176,112,255,0.5)] shrink-0" strokeWidth={2.25} />
              <span>
                Your <span className="font-bold text-amber-300">{streakSave.previous_streak}-day streak</span> broke. Save it before midnight UTC.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={restoreStreak}
                disabled={restoring}
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-bold transition-all duration-200 disabled:opacity-50"
                data-testid="streak-save-restore"
              >
                {restoring ? "Saving..." : `Save ${streakSave.previous_streak}-day streak`}
              </button>
              <button
                onClick={dismissStreak}
                disabled={restoring}
                className="text-zinc-500 hover:text-zinc-300 p-1 transition-colors"
                aria-label="Dismiss"
                data-testid="streak-save-dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero strip */}
      {gateOpen && <StartHereGate onSkip={skipGate} />}
      <section className={`relative border-b border-purple-500/10 ${gateOpen ? "hidden" : ""}`}>
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(168,85,247,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(168,85,247,0.25), transparent 50%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 relative">
          <div className="font-mono text-sm uppercase tracking-[0.3em] text-purple-400 mb-6">
            The vault · {VAULT.promptsFmt} prompts · {VAULT.tracks} tracks
          </div>
          <h1
            className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl"
            data-testid="dashboard-heading"
          >
            From AI idea to{" "}
            <span className="text-purple-400 drop-shadow-[0_0_30px_rgba(168,85,247,0.4)]">
              sellable offer.
            </span>
          </h1>
          <p className="mt-5 max-w-3xl text-base text-zinc-300 md:text-lg" data-testid="dashboard-positioning">Choose a business problem. Follow the playbook. Build the solution. Take it to market. The prompts are the engine — the outcome is the product.</p>
          <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="dashboard-ownership-row">
            <VaultDownloadButton isLifetime={user?.tier === "lifetime"} />
            <ModelBadge testId="dashboard-model-badge" />
          </div>
          <div className="mt-8 max-w-3xl"><StartHereBanner /></div>

          {/* The system, in three moves — designed to be screenshot-friendly + viral */}
          <div className="mt-12 md:mt-16 relative z-40" data-testid="dashboard-system-flow">
            <div className="text-purple-400 text-xs md:text-sm tracking-[0.35em] uppercase mb-6 md:mb-8">
              The system · 3 moves · hover for detail
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-baseline gap-y-8 md:gap-x-10 lg:gap-x-14">
              {[
                {
                  verb: "Pick.",
                  sub: "a track",
                  accent: false,
                  align: "left",
                  info: `${VAULT.tracks} tracks and counting. Pick the one your next 90 days actually need — or start with the AI Side Hustle Ideas track to narrow the field to one clear path. From content and commerce to micro-training, trades systems and local-business consulting, each track is sequenced from niche-finder to performance audit.`,
                },
                {
                  verb: "Paste.",
                  sub: "the prompts",
                  accent: false,
                  align: "left",
                  info: "When we say paste, we mean borrow. Every prompt in here has been structured and quality-reviewed against a documented editorial methodped a real product. Copy it into your model of choice — ChatGPT, Claude, Gemini — edit the [placeholders], and let the AI do the heavy lifting. No reinventing the wheel.",
                },
                {
                  verb: "Profit.",
                  sub: "the income",
                  accent: true,
                  align: "right",
                  info: "The system only counts when something is live and earning. By the end of a track you've got a real asset out in the world — an e-book on KDP, a course filling cohorts, a store taking orders — not another half-finished idea sitting in your notes app.",
                },
              ].map((s, i, arr) => (
                <Fragment key={s.verb}>
                  {i > 0 && (
                    <ArrowRight
                      size={32}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className="text-purple-500/70 hidden md:block self-center shrink-0"
                    />
                  )}
                  <SystemStep
                    verb={s.verb}
                    sub={s.sub}
                    accent={s.accent}
                    info={s.info}
                    align={s.align}
                    testId={`system-step-${i + 1}`}
                  />
                </Fragment>
              ))}
            </div>
          </div>

          {/* Founder Announcement removed Feb 6, 2026 — was going stale on each vault update. */}

          {/* Founder's Gift banner + Elite Bonus card retired Feb 17, 2026 —
              both moved inline into the tracks grid below so paid tiers see
              them as regular cards rather than "special" gold boxes. */}
        </div>
      </section>

      {/* HOW-TO walkthrough — shown only to free-tier members who are new to the vault */}
      {!isPaid && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14" data-testid="how-to-slot">
          <HowToWalkthrough />
        </section>
      )}

      {/* Fastest Path to £1K — top-10 curated tracks (paid: unlocked; free: locked teaser) */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16" data-testid="dashboard-journey-slot"><JourneyView /></section>

      <FastestPathToThousand categories={categories} isPaid={isPaid} />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14" data-testid="dashboard-fast-monetisation-slot">
        <FastMonetisationDiscoveryBanner
          source="dashboard_featured_tile"
          rootTestId="dashboard-fast-monetisation-tile"
          ctaTestId="dashboard-fast-monetisation-tile-button"
          compact
        />
      </section>

      {/* 4-Week £1K Cohort promo — paid members only */}
      {isPaid && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="cohort-promo-slot">
          <Link
            to="/cohort"
            className="group block relative border border-amber-400/30 hover:border-amber-400/70 bg-gradient-to-r from-amber-500/[0.05] via-transparent to-amber-500/[0.05] hover:from-amber-500/[0.09] p-6 md:p-8 transition-all duration-300 overflow-hidden"
            data-testid="cohort-promo-card"
          >
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-amber-300 text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 font-mono">
                  <Flame size={12} className="animate-pulse" strokeWidth={2.5} />
                  4-Week Cohort
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-black tracking-tighter leading-[1.05] text-white mb-2">
                  Want a guided 28-day sprint to ship your first sellable product?
                </h3>
                <p className="text-zinc-400 text-sm md:text-base max-w-2xl">
                  Rolling 28-day cohort. Daily tasks. Drip emails at 09:05 UTC. Join any day — no waiting for Monday.
                </p>
              </div>
              <span className="inline-flex items-center gap-3 self-start md:self-auto text-amber-300 group-hover:text-amber-200 text-sm tracking-[0.25em] uppercase font-bold transition-all duration-300 group-hover:gap-4 shrink-0">
                Enter cohort
                <ArrowUpRight size={20} />
              </span>
            </div>
          </Link>

          <Link
            to="/chrome-extension"
            className="group block relative border border-purple-500/30 hover:border-purple-500/70 bg-gradient-to-r from-purple-500/[0.05] via-transparent to-purple-500/[0.05] hover:from-purple-500/[0.09] p-6 md:p-8 transition-all duration-300 overflow-hidden"
            data-testid="chrome-extension-promo-card"
          >
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-purple-300 text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 font-mono">
                  <Sparkles size={12} className="animate-pulse" strokeWidth={2.5} />
                  Chrome extension · v1.0
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-black tracking-tighter leading-[1.05] text-white mb-2">
                  Search the vault inside ChatGPT.
                </h3>
                <p className="text-zinc-400 text-sm md:text-base max-w-2xl">
                  Cmd-K palette that pastes any of your {VAULT.promptsFmt} prompts straight into ChatGPT&apos;s composer. Ctrl+Shift+V.
                </p>
              </div>
              <span className="inline-flex items-center gap-3 self-start md:self-auto text-purple-300 group-hover:text-purple-200 text-sm tracking-[0.25em] uppercase font-bold transition-all duration-300 group-hover:gap-4 shrink-0">
                Install
                <ArrowUpRight size={20} />
              </span>
            </div>
          </Link>
        </section>
      )}

      <NewInVaultAnnouncement variant="dashboard" />

      <section className="mx-auto mb-10 max-w-7xl px-6 md:px-10" data-testid="dashboard-audit-card">
        <Link to="/audit" className="group flex flex-col gap-4 border border-purple-500/30 bg-[#06040b] p-6 transition-colors hover:border-purple-400 sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-audit-link">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300">Not sure where to start?</div>
            <h2 className="mt-2 font-display text-xl font-black text-white sm:text-2xl">Run the AI Opportunity Audit</h2>
            <p className="mt-1 max-w-2xl text-sm text-zinc-400">Five minutes. A readiness score, your principal problem and the tracks to open first — saved to your account.</p>
          </div>
          <span className="inline-flex min-h-11 items-center gap-2 bg-purple-500 px-5 text-xs font-black uppercase tracking-widest text-black group-hover:bg-purple-400">Start audit <ArrowUpRight size={16} /></span>
        </Link>
      </section>

      {/* Categories grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20">
        {/* Filter pills — sticky under the header so users can flip categories fast */}
        {!loading && categories.length > 0 && (
          <div
            className="sticky top-0 z-20 -mx-6 md:-mx-10 mb-6 md:mb-8 bg-black/85 backdrop-blur-md border-b border-purple-500/15 px-6 md:px-10 py-3 md:py-4"
            data-testid="category-filter-pills"
          >
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-hide">
              {CATEGORY_PILLS.map((p) => {
                const count = pillCounts[p.key] ?? 0;
                const active = activeFilter === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setActiveFilter(p.key)}
                    aria-pressed={active}
                    className={`shrink-0 inline-flex min-h-11 md:min-h-0 items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 border text-[10px] md:text-xs tracking-[0.2em] uppercase font-bold transition-all duration-200 ${
                      active
                        ? "border-purple-400 bg-purple-500/20 text-purple-100 shadow-[0_0_15px_-4px_rgba(168,85,247,0.6)]"
                        : "border-zinc-800 bg-transparent text-zinc-400 hover:border-purple-500/50 hover:text-purple-200"
                    }`}
                    data-testid={`filter-pill-${p.key}`}
                  >
                    <span className="hidden md:inline">{p.label}</span>
                    <span className="md:hidden">{p.short}</span>
                    <span
                      className={`text-[9px] md:text-[10px] font-mono tracking-normal ${
                        active ? "text-purple-300" : "text-zinc-500"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* View toggle */}
        <div className="flex items-center justify-between mb-8" data-testid="view-toggle-bar">
          <span className="text-zinc-400 text-sm tracking-wide">
            {loading
              ? "Loading..."
              : activeFilter === "all"
                ? `${categories.length} tracks`
                : `${filteredCategories.length} in ${activePillLabel}`}
          </span>
          <div
            className="inline-flex border border-zinc-800 bg-[#0a0a0a]"
            role="tablist"
            aria-label="Dashboard view"
          >
            <button
              type="button"
              onClick={() => changeView("grid")}
              aria-pressed={view === "grid"}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm tracking-wide transition-all duration-200 ${
                view === "grid"
                  ? "bg-purple-500/15 text-purple-300 border-r border-purple-500/40"
                  : "text-zinc-400 hover:text-purple-300 border-r border-zinc-800"
              }`}
              data-testid="view-toggle-grid"
            >
              <LayoutGrid size={16} />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              type="button"
              onClick={() => changeView("list")}
              aria-pressed={view === "list"}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm tracking-wide transition-all duration-200 ${
                view === "list"
                  ? "bg-purple-500/15 text-purple-300"
                  : "text-zinc-400 hover:text-purple-300"
              }`}
              data-testid="view-toggle-list"
            >
              <List size={16} />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-400 text-base" data-testid="categories-loading">
            Loading the vault...
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900" data-testid="categories-grid">
            {/* Elite Track — pinned to the front of the grid as a special bonus card.
                Shows only when the "All" filter is active, so filtered views
                stay focused on the selected category. */}
            {activeFilter === "all" && (
            <Link
              to="/vault/elite"
              className="group bg-gradient-to-br from-[#12061e] via-[#0a0a0a] to-[#150c05] hover:from-[#1a0929] hover:to-[#1f1108] p-10 md:p-12 relative transition-all duration-300 border border-amber-400/30 hover:border-amber-400/70 flex flex-col shadow-[0_0_25px_-10px_rgba(176,112,255,0.45)]"
              data-testid="category-card-elite"
            >
              <div className="relative flex items-start justify-between mb-10">
                <CategoryIcon lucide="crown" size={88} />
                <div className="flex flex-col items-end gap-2 mt-1">
                  <span className="text-amber-300 text-sm tracking-wide">Bonus Track</span>
                  <span className="inline-flex items-center gap-1 bg-amber-500/15 border border-amber-400/60 text-amber-200 text-[10px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold">
                    <Sparkles size={10} strokeWidth={2.5} />
                    Elite
                  </span>
                </div>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white leading-[1.1]">
                Elite Prompts
              </h3>
              <p className="text-zinc-300 text-lg leading-relaxed mb-10 flex-1">
                Deep-cut prompts reserved for lifetime members and 7-day-streak keepers. Different every quarter.
              </p>
              <span className="text-amber-300 group-hover:text-amber-200 text-base font-medium inline-flex items-center gap-2 transition-all duration-300 group-hover:gap-3">
                Open bonus <ArrowUpRight size={20} />
              </span>
            </Link>
            )}
            {/* Founder's Special — paid-only pinned bonus card. Filtered from the
                main loop below so it never renders twice. Shows only when "All"
                filter or when its own category (commerce) is active. */}
            {(activeFilter === "all" || activeFilter === "commerce") && filteredCategories.find((c) => c.founders_special) && (() => {
              const fs = filteredCategories.find((c) => c.founders_special);
              const shortName = fs.name?.split(" · ")[0] || fs.name;
              return (
                <Link
                  to={`/vault/${fs.slug}`}
                  className="group bg-gradient-to-br from-[#1a0929] via-[#0a0a0a] to-[#12061e] hover:from-[#22103a] hover:to-[#180a29] p-10 md:p-12 relative transition-all duration-300 border border-purple-400/40 hover:border-purple-400/80 flex flex-col shadow-[0_0_28px_-10px_rgba(168,85,247,0.55)]"
                  data-testid={`category-card-${fs.slug}`}
                >
                  <div className="relative flex items-start justify-between mb-10">
                    <CategoryIcon image={fs.image_icon} lucide={fs.icon} size={88} />
                    <div className="flex flex-col items-end gap-2 mt-1">
                      <span className="text-purple-200 text-sm tracking-wide">Bonus Track</span>
                      <span className="inline-flex items-center gap-1 bg-purple-500/20 border border-purple-400/70 text-purple-100 text-[10px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold shadow-[0_0_15px_rgba(168,85,247,0.4)]">
                        <Sparkles size={10} strokeWidth={2.5} />
                        Founder&apos;s Special
                      </span>
                    </div>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white leading-[1.1]">
                    {shortName}
                  </h3>
                  <p className="text-zinc-300 text-lg leading-relaxed mb-10 flex-1">
                    {fs.tagline}
                  </p>
                  <span className="text-purple-200 group-hover:text-purple-100 text-base font-medium inline-flex items-center gap-2 transition-all duration-300 group-hover:gap-3">
                    {isPaid ? "Open bonus" : "Preview & unlock"} <ArrowUpRight size={20} />
                  </span>
                </Link>
              );
            })()}
            {tieredCategories.filter((c) => !c.founders_special).map((c, i, arr) => {
              const tier = c.tier || "classic";
              const header = activeFilter === "all" && (i === 0 || (arr[i - 1].tier || "classic") !== tier) ? <TierHeader key={`hdr-${tier}`} tier={tier} count={tierCounts[tier]} /> : null;
              const locked = !isPaid && c.order > FREE_LIMIT;
              const to = `/vault/${c.slug}`;
              const done = countFor(c.slug);
              return (<Fragment key={c.slug}>{header}
                <Link
                  to={to}
                  className="group bg-[#0a0a0a] hover:bg-[#0d0a14] p-10 md:p-12 relative transition-colors duration-300 border border-transparent hover:border-purple-500/40 flex flex-col"
                  data-testid={`category-card-${c.slug}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                  <div className="relative flex items-start justify-between mb-10">
                    <CategoryIcon image={c.image_icon} lucide={c.icon} size={88} />
                    <div className="flex min-w-0 max-w-full flex-col items-end gap-2 mt-1">
                      <span className="text-zinc-500 text-sm tracking-wide">
                        Track {String(c.order).padStart(2, "0")}
                      </span>
                      {isNewTrack(c.shipped_at) && (
                        <span
                          className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-500/30 to-fuchsia-500/30 border border-purple-400/70 text-purple-100 text-[10px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold shadow-[0_0_18px_rgba(168,85,247,0.55)] animate-pulse"
                          data-testid={`card-new-${c.slug}`}
                          title="Just dropped — new this week"
                        >
                          <Sparkles size={10} strokeWidth={2.5} />
                          New
                        </span>
                      )}
                      <DemandBadge signal={c.demand_signal} slug={c.slug} />
                      {locked && (
                        <span className="inline-flex items-center gap-1 bg-purple-500/15 border border-purple-500/50 text-purple-300 text-[10px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold" data-testid={`card-locked-${c.slug}`}>
                          <Lock size={10} strokeWidth={2.5} />
                          Lifetime Access
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white leading-[1.1]">
                    {c.name}
                  </h3>
                  <p className="text-zinc-300 text-lg leading-relaxed mb-10 flex-1">
                    {c.tagline}
                  </p>

                  {planDays[c.slug] > 0 && (
                    <div className="mb-4" data-testid={`plan-progress-${c.slug}`}>
                      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
                        <span>30-day plan</span><span className="text-emerald-300">Day {planDays[c.slug]} / 30</span>
                      </div>
                      <div className="h-1 w-full bg-zinc-900"><div className="h-1 bg-emerald-400 transition-[width] duration-500" style={{ width: `${Math.min(100, Math.round((planDays[c.slug] / 30) * 100))}%` }} /></div>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-6 border-t border-zinc-900">
                    <span className="text-zinc-400 text-base" data-testid={`track-card-status-${c.slug}`}>
                      {readyStates[c.slug]?.ready_to_sell ? <span className="text-purple-300">Ready to sell</span> : done >= c.prompt_count && c.prompt_count > 0 ? <span className="text-white">Completed</span> : done > 0 ? `In progress · ${done} of ${c.prompt_count}` : `Not started · ${c.prompt_count} prompts`}
                      {c.superseded_by && <span className="ml-2 text-xs text-amber-200/80">· see {c.superseded_by.name}</span>}
                    </span>
                    <span className="text-purple-400 group-hover:text-purple-300 text-base font-medium inline-flex items-center gap-2 transition-all duration-300 group-hover:gap-3">
                      {locked ? "Preview" : "Open"}
                      <ArrowUpRight size={20} />
                    </span>
                  </div>
                </Link>
              </Fragment>);
            })}
            {activeFilter === "all" && <FutureTracks />}
          </div>
        ) : (
          <ul
            className="divide-y divide-zinc-900 border-y border-zinc-900"
            data-testid="categories-list"
          >
            {/* Elite Track — pinned to the front of the list as a special bonus row.
                Shows only when the "All" filter is active. */}
            {activeFilter === "all" && (
            <li>
              <Link
                to="/vault/elite"
                className="group flex items-center gap-5 md:gap-8 px-2 md:px-6 py-5 md:py-6 hover:bg-[#150c05] transition-colors duration-200"
                data-testid="category-row-elite"
              >
                <span className="text-amber-300 text-sm tracking-wide w-10 shrink-0 font-mono">★</span>
                <CategoryIcon lucide="crown" size={56} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight truncate inline-flex items-center gap-3">
                    Elite Prompts
                    <span className="inline-flex items-center gap-1 bg-amber-500/15 border border-amber-400/60 text-amber-200 text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 leading-none font-bold shrink-0">
                      <Sparkles size={8} strokeWidth={2.5} /> Elite
                    </span>
                  </h3>
                  <p className="text-zinc-400 text-base mt-1 truncate">
                    Deep-cut prompts reserved for lifetime members and 7-day-streak keepers.
                  </p>
                </div>
                <span className="hidden md:inline text-amber-300/70 text-sm tracking-wide w-24 text-right shrink-0">Bonus</span>
                <span className="text-amber-300 group-hover:text-amber-200 text-base font-medium inline-flex items-center gap-2 transition-all duration-200 group-hover:gap-3 shrink-0">
                  Open <ArrowUpRight size={18} />
                </span>
              </Link>
            </li>
            )}
            {filteredCategories.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/vault/${c.slug}`}
                  className="group flex items-center gap-5 md:gap-8 px-2 md:px-6 py-5 md:py-6 hover:bg-[#0d0a14] transition-colors duration-200"
                  data-testid={`category-row-${c.slug}`}
                >
                  <span className="text-zinc-600 text-sm tracking-wide w-10 shrink-0 font-mono">
                    {String(c.order).padStart(2, "0")}
                  </span>
                  <CategoryIcon image={c.image_icon} lucide={c.icon} size={56} className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight truncate inline-flex items-center gap-3">
                      {c.name}
                      {isNewTrack(c.shipped_at) && (
                        <span
                          className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-500/30 to-fuchsia-500/30 border border-purple-400/70 text-purple-100 text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 leading-none font-bold shadow-[0_0_14px_rgba(168,85,247,0.55)] animate-pulse shrink-0"
                          data-testid={`row-new-${c.slug}`}
                        >
                          <Sparkles size={8} strokeWidth={2.5} />
                          New
                        </span>
                      )}
                      <DemandBadge signal={c.demand_signal} slug={`row-${c.slug}`} size={8} />
                      {planDays[c.slug] > 0 && (
                        <span className="inline-flex items-center gap-1 border border-emerald-400/50 text-emerald-300 text-[10px] tracking-[0.2em] uppercase px-2 py-1 leading-none font-bold" data-testid={`plan-progress-row-${c.slug}`}>
                          Plan · day {planDays[c.slug]}/30
                        </span>
                      )}
                    </h3>
                    <p className="text-zinc-400 text-base mt-1 truncate">
                      {c.tagline}
                    </p>
                  </div>
                  <span className="hidden md:inline text-zinc-400 text-sm tracking-wide w-24 text-right shrink-0">
                    {c.prompt_count} prompts
                  </span>
                  <span className="text-purple-400 group-hover:text-purple-300 text-base font-medium inline-flex items-center gap-2 transition-all duration-200 group-hover:gap-3 shrink-0">
                    <span className="hidden sm:inline">Open</span>
                    <ArrowUpRight size={20} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* Templates tile — pairs with the prompt library */}
        <Link
          to="/vault/templates"
          className="mt-12 block group relative border border-purple-500/30 hover:border-purple-500/70 bg-[#0a0a0a] hover:bg-[#0d0a14] p-8 md:p-10 transition-all duration-300 overflow-hidden"
          data-testid="dashboard-templates-tile"
        >
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 85% 20%, rgba(168,85,247,0.65), transparent 55%)",
            }}
          />
          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="text-purple-400 text-xs tracking-[0.3em] uppercase mb-3 font-mono">
                · Bonus pack ·
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-[1.05] text-white mb-3">
                Templates, done-for-you.
              </h3>
              <p className="text-zinc-400 text-base max-w-xl">
                Notion systems, Canva mockups & swipe files that pair with every track. {isPaid ? "Already unlocked." : "Locked on Free."}
              </p>
            </div>
            <span className="inline-flex items-center gap-3 self-start md:self-auto text-purple-300 group-hover:text-purple-200 text-sm tracking-[0.25em] uppercase font-bold transition-all duration-300 group-hover:gap-4">
              {isPaid ? "Browse templates" : "Preview & unlock"}
              <ArrowUpRight size={20} />
            </span>
          </div>
        </Link>

        {/* Start Here · study room strip */}
        <StartHereStrip />

        {/* Next drops · roadmap preview */}
        <NextDropsPreview categories={categories} loading={loading} />
        <AffiliateInvite surface="dashboard" className="mt-12" />
      </section>

      <footer className="border-t border-zinc-900 mt-12 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            Stag Vault · A quiet edge for builders · est. 2026
          </p>
          <p className="text-zinc-600 text-sm">
            Prompts work with any major LLM (GPT, Claude, Gemini).
          </p>
        </div>
      </footer>
    </div>
  );
}
